import { Injectable } from '@angular/core';
// We will use pdf-lib to load and draw on the base PDF template.
// Using dynamic import to avoid increasing initial bundle size if not used.

@Injectable({
  providedIn: 'root'
})
export class PdfGeneratorService {

  constructor() { }

  // Signature placement and size on the base template (in logical pixels of 1414x2000)
  // Adjust these values after visual QA if needed.
  private readonly SIGNATURE_LAYOUT = {
    xPx: 270,
    yPx: 1550,
    widthPx: 420,
    heightPx: 140,
    label: '',
    labelOffsetYPx: 16,
    labelFontSize: 18,
  } as const;

  /**
   * Génère un PDF du devis avec la signature électronique
   * @param formData - Données du formulaire
   * @param signature - Données de signature
   * @returns Promise<Blob> - Le PDF généré
   */
  async generateSignedQuotePDF(formData: any, signature: any): Promise<Blob> {
    // Produce the real PDF using the same engine as preview, with embedded signature image
    let signatureDataUrl: string | undefined;
    if (signature?.method === 'initials' && typeof signature.data === 'string') {
      // Render initials text to a PNG so it can be embedded as an image
      signatureDataUrl = this.renderInitialsToDataUrl(signature.data);
    } else if ((signature?.method === 'draw' || signature?.method === 'upload') && typeof signature?.data === 'string') {
      // Expect a data URL (PNG/JPEG) from canvas or uploaded image
      signatureDataUrl = signature.data;
    }

    return this.buildFilledQuotePDF(formData, { signatureDataUrl });
  }

  /**
   * Build a previewable quote PDF by overlaying client data on the base template.
   * It respects the base template pixel size: 1414 x 2000 px.
   * Coordinates passed to draw functions should use that pixel space.
   */
  async buildFilledQuotePDF(formData: any, options?: { signatureDataUrl?: string }): Promise<Blob> {
    // Lazy-load pdf-lib only when needed
    const { PDFDocument, StandardFonts, rgb } = await import('pdf-lib');

    // Load base template from assets
    const baseUrl = 'assets/docs/quote_aps_.pdf';
    const basePdfBytes = await fetch(baseUrl).then(r => r.arrayBuffer());

    const pdfDoc = await PDFDocument.load(basePdfBytes);
    const pages = pdfDoc.getPages();
    const page = pages[0];

  // The provided template logical size in pixels (from user): 1414 x 2000
  // PDF uses points where 72pt == 1 inch. We will compute a scale to map pixels -> points
  const targetWidthPx = 1414;
  const targetHeightPx = 2000;

    // Actual page size in points
    const { width: pageWidthPt, height: pageHeightPt } = page.getSize();

    // scale factor: px -> pt
    const sx = pageWidthPt / targetWidthPx;
    const sy = pageHeightPt / targetHeightPx;
    const scale = (px: number, axis: 'x' | 'y') => (axis === 'x' ? px * sx : px * sy);

    const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
    const fontBold = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
    // Try to load custom branding font (coco-heavy.ttf). Ensure the font file exists under assets/fonts/.
    let fontCoco: any = null;
    try {
      const cocoBytes = await fetch('assets/fonts/coco-heavy.ttf').then(r => r.ok ? r.arrayBuffer() : Promise.reject());
      fontCoco = await pdfDoc.embedFont(cocoBytes);
    } catch (_) {
      // Fallback silently if font missing
    }

    // Helpers to draw text using pixel coordinates from top-left origin
    const drawTextPx = (
      text: string,
      xPx: number,
      yPxFromTop: number,
      opts?: { size?: number; color?: { r: number; g: number; b: number }; bold?: boolean; maxWidthPx?: number }
    ) => {
      const size = opts?.size ?? 28; // default readable size
      const color = opts?.color ?? { r: 0, g: 0, b: 0 };
      const fontToUse = opts?.bold ? fontBold : font;

      // Convert top-left pixel y to PDF bottom-left origin in points
      const xPt = scale(xPx, 'x');
      const yPt = pageHeightPt - scale(yPxFromTop, 'y');
      const maxWidthPt = opts?.maxWidthPx ? scale(opts.maxWidthPx, 'x') : undefined;

      page.drawText(text, {
        x: xPt,
        y: yPt,
        size: size * ((sx + sy) / 2), // scale font roughly equally based on average scale
        color: rgb(color.r / 255, color.g / 255, color.b / 255),
        font: fontToUse,
        maxWidth: maxWidthPt,
      });
    };

    // Draw text to fit inside a bounding box (top-left origin) in template pixels
    const drawTextBoxPx = (
      text: string,
      xPx: number,
      yPxFromTop: number,
      boxWidthPx: number,
      boxHeightPx: number,
      opts?: { bold?: boolean; color?: { r: number; g: number; b: number } }
    ) => {
      const color = opts?.color ?? { r: 0, g: 0, b: 0 };
      const fontToUse = opts?.bold ? fontBold : font;
      // Start with height fit, then reduce to fit width
      let sizePx = Math.max(8, boxHeightPx * 0.9);
      for (let i = 0; i < 40; i++) {
        const widthAt = fontToUse.widthOfTextAtSize(text, sizePx);
        if (widthAt <= boxWidthPx) break;
        sizePx -= Math.ceil(sizePx * 0.06);
        if (sizePx < 8) break;
      }
      const xPt = scale(xPx, 'x');
      const yPt = pageHeightPt - scale(yPxFromTop, 'y') - scale(boxHeightPx * 0.1, 'y');
      page.drawText(text, {
        x: xPt,
        y: yPt,
        size: sizePx * ((sx + sy) / 2),
        color: rgb(color.r / 255, color.g / 255, color.b / 255),
        font: fontToUse,
        maxWidth: scale(boxWidthPx, 'x'),
      });
    };

    // Right align a single line of text within a box width at x start (top-left origin)
    const drawRightAlignedPx = (
      text: string,
      xStartPx: number,
      yPxFromTop: number,
      boxWidthPx: number,
      sizePx: number,
      opts?: { bold?: boolean; color?: { r: number; g: number; b: number } }
    ) => {
      const color = opts?.color ?? { r: 0, g: 0, b: 0 };
      const fontToUse = opts?.bold ? fontBold : font;
      const textWidthPx = fontToUse.widthOfTextAtSize(text, sizePx);
      const xPx = xStartPx + Math.max(0, boxWidthPx - textWidthPx);
      const xPt = scale(xPx, 'x');
      const yPt = pageHeightPt - scale(yPxFromTop, 'y');
      page.drawText(text, {
        x: xPt,
        y: yPt,
        size: sizePx * ((sx + sy) / 2),
        color: rgb(color.r / 255, color.g / 255, color.b / 255),
        font: fontToUse,
      });
    };

    // Format currency in fr-FR (EUR)
    const formatCurrency = (value: number) =>
      (Number.isFinite(value) ? value : 0).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' });

    // Fixed labels according to the 1414x2000 witness image
    drawTextBoxPx('DEVIS', 147, 167, 193, 54, { bold: true });
    // (Will draw company block later at previous CLIENT location)
  // ------------------------------------------------------------------
  // Table headers: DESCRIPTION | DURÉE | PRIX | QUANTITÉ | TOTAL HT
  // Les coordonnées ci-dessous sont maintenant figées (valeurs déjà
  // recalculées depuis la base 2481px vers l'espace logique 1414px).
  // DESCRIPTION wrap autorisé; autres colonnes en une ligne.
  // ------------------------------------------------------------------
    const COLOR_WHITE = { r: 255, g: 255, b: 255 };
    const COLOR_DARK_GREEN = { r: 0, g: 100, b: 0 };

  const tableTopY = 625;
  const headerHeight = 30;

  // Positions finales (hard-coded après scaling 2481→1414)
  const colDescriptionX = 154;
  const colDescriptionWidth = 627;
  const colDurationX = 792;
  const colDurationWidth = 143;
  const colPriceX = 946;
  const colPriceWidth = 96;
  const colQuantityX = 1054;
  const colQuantityWidth = 84;
  const colTotalX = 1150;
  const colTotalWidth = 103;

  // Slight safety adjustment: ensure columns are strictly increasing and within page width
  // (Optional: adjust if overlaps occur after rounding)

  const HEADER_FONT_SIZE = 14; // Titres
  const DATA_FONT_SIZE = 12;   // Contenu des lignes (titres - 2)
  drawTextPx('DESCRIPTION', colDescriptionX, tableTopY, { size: HEADER_FONT_SIZE, bold: true, color: COLOR_WHITE, maxWidthPx: colDescriptionWidth });
  drawTextPx('DURÉE', colDurationX, tableTopY, { size: HEADER_FONT_SIZE, bold: true, color: COLOR_WHITE, maxWidthPx: colDurationWidth });
  // Header PRIX now white (only data cells will be green)
  drawTextPx('PRIX', colPriceX, tableTopY, { size: HEADER_FONT_SIZE, bold: true, color: COLOR_WHITE, maxWidthPx: colPriceWidth });
  drawTextPx('QUANTITÉ', colQuantityX, tableTopY, { size: HEADER_FONT_SIZE, bold: true, color: COLOR_WHITE, maxWidthPx: colQuantityWidth });
  drawTextPx('TOTAL HT', colTotalX, tableTopY, { size: HEADER_FONT_SIZE, bold: true, color: COLOR_WHITE, maxWidthPx: colTotalWidth });
    drawTextBoxPx('Signature', 138, 1730, 80, 18, { bold: true });
  // Aligner sur une seule ligne : "Applisound Société Anonyme IDE: CHE-123.456.789"
  // Largeur suffisante pour que tout tienne sans wrap (≈ 900px disponible jusqu'au bord droit)
  drawTextBoxPx('Applisound · Société Anonyme · IDE: CHE-123.456.789 · Switzerland', 473, 1880, 900, 18, { bold: true });

  // Swap zones: Place company block at old CLIENT area (right), and client info at old company area (left)
  const pi = (formData && formData.personalInfo) ? formData.personalInfo : {};
  const clientName = [pi?.prenom, pi?.nom].filter(Boolean).join(' ').trim();
  const clientCompany = (pi?.entreprise || '').toString();
  const clientEmail = (pi?.email || '').toString();
  const clientPhone = (pi?.telephone || '').toString();

  // New right-side company block (uses original client area starting at y=220)
  const companyBlockY = 220;
  if (fontCoco) {
    const brandXPt = scale(910, 'x');
    const brandYPt = pageHeightPt - scale(companyBlockY, 'y');
    page.drawText('APPLISOUND SA', {
      x: brandXPt,
      y: brandYPt,
      size: 30 * ((sx + sy) / 2),
      font: fontCoco,
      color: rgb(0, 0, 0)
    });
  } else {
    drawTextBoxPx('APPLISOUND SA', 910, companyBlockY, 430, 28, { bold: true });
  }
  drawTextBoxPx('Rue des entreprises 18', 910, companyBlockY + 34, 430, 22);
  drawTextBoxPx('2006 Neuchatel', 910, companyBlockY + 60, 430, 22);

  // Left-side client block (previous company location starting around y=406)
  const clientLeftY = 406;
  drawTextBoxPx('CLIENT', 142, clientLeftY, 220, 24, { bold: true });
  let lineOffset = 34;
  if (clientName) { drawTextBoxPx(clientName, 142, clientLeftY + lineOffset, 400, 22); lineOffset += 26; }
  if (clientCompany) { drawTextBoxPx(clientCompany, 142, clientLeftY + lineOffset, 400, 22); lineOffset += 26; }
  if (clientEmail) { drawTextBoxPx(clientEmail, 142, clientLeftY + lineOffset, 400, 22); lineOffset += 26; }
  if (clientPhone) { drawTextBoxPx(clientPhone, 142, clientLeftY + lineOffset, 400, 22); }

    // Signature placeholder if provided (bottom area)
    if (options?.signatureDataUrl) {
      const sigUrl = options.signatureDataUrl;
      // Determine image type and embed accordingly
      const isJpeg = /^data:image\/(jpg|jpeg)/i.test(sigUrl);
      const imgBytes = await fetch(sigUrl).then(r => r.arrayBuffer());
      const img = isJpeg ? await pdfDoc.embedJpg(imgBytes) : await pdfDoc.embedPng(imgBytes);

      // Preserve aspect ratio: fit image within bounding box without upscaling
  const { widthPx: maxW, heightPx: maxH, xPx, yPx } = this.SIGNATURE_LAYOUT;
  let drawW: number = maxW;
  let drawH: number = maxH;
      try {
        const dims = await this.loadImageDimensions(sigUrl);
        const scaleFit = Math.min(maxW / dims.width, maxH / dims.height, 1); // reduce only, never upscale
        drawW = Math.round(dims.width * scaleFit);
        drawH = Math.round(dims.height * scaleFit);
      } catch (_) {
        // If we fail to read dimensions (shouldn't happen for data URLs), fallback to box size
        drawW = maxW;
        drawH = maxH;
      }

      page.drawImage(img, {
        x: scale(xPx, 'x'),
        y: pageHeightPt - scale(yPx, 'y') - scale(drawH, 'y'),
        width: scale(drawW, 'x'),
        height: scale(drawH, 'y'),
      });
      // No extra signature label here; the template label is already placed above
    }

    // Items table: DESCRIPTION | DURÉE | PRIX | QUANTITÉ | TOTAL HT (line)
    const items: Array<{ description: string; duration: string; unitPrice: number; quantity: number; lineTotal: number }> =
      Array.isArray(formData?.selectedServices)
        ? formData.selectedServices.map((s: any) => {
            const unit = Number(s?.price) || 0;
            const qty = Number(s?.quantity) || 1;
            return {
              description: (s?.serviceName || '').toString(),
              duration: (s?.durationLabel || '').toString(),
              unitPrice: unit,
              quantity: qty,
              lineTotal: unit * qty,
            };
          })
        : [];

  const rowStartY = tableTopY + headerHeight + 20; // start below headers
  const rowHeight = 42;
  // textSize replaced by DATA_FONT_SIZE for uniform smaller content

    let cursorY = rowStartY;
    let totalHT = 0;
    if (items.length === 0) {
      drawTextBoxPx('Aucun service sélectionné', colDescriptionX, cursorY, (colTotalX + colTotalWidth) - colDescriptionX, 28, { bold: false });
      cursorY += rowHeight;
    } else {
      for (const it of items) {
        // Description and duration boxes (wrapping handled by drawTextBoxPx)
  // Description may wrap (height adapt: we use two passes if needed). For simplicity keep single line height logic; wrapping is handled by maxWidth.
  // Use smaller boxHeight so resulting font ~12px (boxHeight * 0.9 in drawTextBoxPx)
  drawTextBoxPx(it.description || '-', colDescriptionX, cursorY, colDescriptionWidth, 14, { bold: false, color: { r: 0, g: 0, b: 0 } });
  drawTextBoxPx(it.duration || '-', colDurationX, cursorY, colDurationWidth, 14, { bold: false, color: { r: 0, g: 0, b: 0 } });
  // Price right aligned (green & bold) size 12
  drawRightAlignedPx(formatCurrency(it.unitPrice), colPriceX, cursorY, colPriceWidth, DATA_FONT_SIZE, { bold: true, color: COLOR_DARK_GREEN });
        // Quantity right aligned
    drawRightAlignedPx(String(it.quantity), colQuantityX, cursorY, colQuantityWidth, DATA_FONT_SIZE, { bold: false });
  // Line total right aligned (also considered a price -> green & bold)
  drawRightAlignedPx(formatCurrency(it.lineTotal), colTotalX, cursorY, colTotalWidth, DATA_FONT_SIZE, { bold: true, color: COLOR_DARK_GREEN });

        totalHT += it.lineTotal;
        cursorY += rowHeight;
        if (cursorY > 1550) break; // avoid signature collision
      }
    }

    // Totals block directly beneath table, aligned to TOTAL HT column
    const tvaRate = 0.20;
    const tva = totalHT * tvaRate;
    const totalTTC = totalHT + tva;
    const totalsY = Math.min(cursorY + 10, 1620);

  const totalLabelWidth = colQuantityWidth + colPriceWidth + colDurationWidth; // span preceding middle columns
  const totalLabelX = colPriceX; // start labels at price column

    const drawTotalRow = (label: string, amount: string, y: number, bold = false) => {
      // label height reduced to approximate 12px font (0.9 * 14 ≈ 12)
      drawTextBoxPx(label, totalLabelX, y, totalLabelWidth, 14, { bold });
      // Amount size: 12 for all; keep bold for emphasis; could bump final total if desired
      drawRightAlignedPx(amount, colTotalX, y, colTotalWidth, DATA_FONT_SIZE, { bold: true, color: COLOR_DARK_GREEN });
    };

    drawTotalRow('Total HT', formatCurrency(totalHT), totalsY, false);
    drawTotalRow(`TVA (${Math.round(tvaRate * 100)}%)`, formatCurrency(tva), totalsY + 26, false);
    drawTotalRow('Total TTC', formatCurrency(totalTTC), totalsY + 52, true);

    const bytes = await pdfDoc.save();
    return new Blob([bytes], { type: 'application/pdf' });
  }

  /**
   * Convert a Blob to an object URL for embedding in an iframe.
   */
  toObjectUrl(blob: Blob): string {
    return URL.createObjectURL(blob);
  }

  /**
   * Construit le contenu du PDF
   */
  private buildPDFContent(formData: any, signature: any): string {
    const personalInfo = formData.personalInfo || {};
    const selectedServices = formData.selectedServices || [];
    
    let totalHT = 0;
    selectedServices.forEach((service: any) => {
      totalHT += service.price * service.quantity;
    });
    
    const totalTTC = totalHT * 1.2;
    const tva = totalHT * 0.2;
    
    return `
DEVIS APPLISOUND
================

Date: ${new Date().toLocaleDateString('fr-FR')}
Numéro de devis: APS-${Date.now()}

INFORMATIONS CLIENT
-------------------
Nom: ${personalInfo.nom} ${personalInfo.prenom}
Email: ${personalInfo.email}
Téléphone: ${personalInfo.telephone || 'Non renseigné'}
Entreprise: ${personalInfo.entreprise || 'Particulier'}
Fonction: ${personalInfo.fonction || 'Non renseigné'}

DESCRIPTION DU PROJET
---------------------
${formData.projectDescription || 'Non renseigné'}

SERVICES COMMANDÉS
------------------
${selectedServices.map((service: any) => 
  `- ${service.serviceName} (${service.durationLabel}) x${service.quantity} : ${service.price * service.quantity}€`
).join('\n')}

RÉCAPITULATIF FINANCIER
-----------------------
Total HT: ${totalHT.toFixed(2)}€
TVA (20%): ${tva.toFixed(2)}€
Total TTC: ${totalTTC.toFixed(2)}€

CONDITIONS DE PAIEMENT
----------------------
- Acompte de 50% à la commande: ${(totalTTC * 0.5).toFixed(2)}€
- Solde à la livraison: ${(totalTTC * 0.5).toFixed(2)}€

SIGNATURE ÉLECTRONIQUE
----------------------
Méthode de signature: ${this.getSignatureMethodLabel(signature.method)}
Date et heure: ${new Date(signature.timestamp).toLocaleString('fr-FR')}
Adresse IP: ${signature.ipAddress}
Navigateur: ${signature.userAgent}

${signature.method === 'initials' ? 
  `Initiales: ${signature.data}` : 
  'Signature graphique intégrée'
}

ACCEPTATION DES CONDITIONS
--------------------------
En signant électroniquement ce devis, le client accepte les conditions générales de vente d'Applisound et confirme sa commande.

Signature valide selon le règlement eIDAS et le Code civil français.

Document généré automatiquement le ${new Date().toLocaleString('fr-FR')}
    `;
  }

  /**
   * Retourne le libellé de la méthode de signature
   */
  private getSignatureMethodLabel(method: string): string {
    switch (method) {
      case 'draw':
        return 'Signature dessinée';
      case 'upload':
        return 'Image de signature importée';
      case 'initials':
        return 'Initiales saisies';
      default:
        return 'Méthode inconnue';
    }
  }

  /**
   * Télécharge le PDF généré
   */
  downloadPDF(blob: Blob, filename: string = 'devis-applisound.pdf'): void {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  /**
   * Valide la conformité légale de la signature électronique
   */
  validateElectronicSignature(signature: any): boolean {
    // Vérifications de conformité selon eIDAS
    const requiredFields = ['method', 'timestamp', 'data', 'ipAddress', 'userAgent'];
    
    for (const field of requiredFields) {
      if (!signature[field]) {
        return false;
      }
    }

    // Vérifier que la signature n'est pas vide
    if (signature.method === 'initials' && signature.data.trim().length < 2) {
      return false;
    }

    // Vérifier que le timestamp est récent (moins de 24h)
    const signatureTime = new Date(signature.timestamp);
    const now = new Date();
    const timeDiff = now.getTime() - signatureTime.getTime();
    const hoursDiff = timeDiff / (1000 * 3600);
    
    if (hoursDiff > 24) {
      return false;
    }

    return true;
  }

  /**
   * Génère un hash de vérification pour la signature
   */
  generateSignatureHash(signature: any): string {
    const dataToHash = `${signature.method}-${signature.timestamp}-${signature.data}-${signature.ipAddress}`;
    // Dans une implémentation réelle, utilisez une fonction de hachage cryptographique
    return btoa(dataToHash).substring(0, 16);
  }

  // Load intrinsic dimensions of an image data URL (px)
  private loadImageDimensions(dataUrl: string): Promise<{ width: number; height: number }> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
      img.onerror = (e) => reject(e);
      img.src = dataUrl;
    });
  }

  // Render initials as a PNG data URL (used for embedding into the PDF)
  private renderInitialsToDataUrl(initials: string): string {
    const canvas = document.createElement('canvas');
    canvas.width = 600;
    canvas.height = 220;
    const ctx = canvas.getContext('2d')!;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = '#000';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    let text = (initials || '').trim();
    let fontSize = 140;
    const maxWidth = canvas.width - 40; // padding
    while (fontSize > 40) {
      ctx.font = `bold ${fontSize}px "Brush Script MT", "Segoe Script", cursive`;
      if (ctx.measureText(text).width <= maxWidth) break;
      fontSize -= 6;
    }
    ctx.font = `bold ${fontSize}px "Brush Script MT", "Segoe Script", cursive`;
    ctx.fillText(text, canvas.width / 2, canvas.height / 2);
    return canvas.toDataURL('image/png');
  }
}